import React from "react";
import StatCard from "../components/StatCard";
import { api, apiUrl } from "../../api/api";
import ReceiptLogo from "../../Assets/logo-receipt.png";
import QRCode from "qrcode";

function n(v) {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}
function money(v) {
  const num = n(v);
  return `$${num.toFixed(2)}`;
}
function pad2(x) {
  return String(x).padStart(2, "0");
}
function formatDateTime(d) {
  const dd = new Date(d);
  return `${dd.getFullYear()}-${pad2(dd.getMonth() + 1)}-${pad2(dd.getDate())} ${pad2(dd.getHours())}:${pad2(
    dd.getMinutes()
  )}`;
}

const API_BASE = (process.env.REACT_APP_API_BASE || "https://nodeapi.sts-lb.net").replace(/\/$/, "");

function getToken() {
  return (
    localStorage.getItem("admin_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("sts_admin_token") ||
    ""
  );
}

async function fetchItemsDirect() {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const res = await fetch(`${API_BASE}/items`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed (${res.status})`;
    throw new Error(msg);
  }

  return Array.isArray(data) ? data : [];
}

async function checkoutDirect(cartList) {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const payload = {
    items: cartList.map((row) => ({ item_id: row.item.id, qty: n(row.qty) })),
  };

  const res = await fetch(`${API_BASE}/sales/checkout`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Checkout failed (${res.status})`;
    throw new Error(msg);
  }

  return data || {};
}

/** ✅ NEW: list sales for History */
async function fetchSalesDirect({ limit = 30, offset = 0 } = {}) {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const res = await fetch(`${API_BASE}/sales?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed (${res.status})`;
    throw new Error(msg);
  }

  return Array.isArray(data) ? data : [];
}

/** ✅ NEW: get a sale details + receipt (to re-print) */
async function fetchSaleDetailsDirect(saleId) {
  const token = getToken();
  if (!token) throw new Error("Missing token. Please login again.");

  const res = await fetch(`${API_BASE}/sales/${saleId}`, {
    headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
  });

  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || text || `Failed (${res.status})`;
    throw new Error(msg);
  }

  return data || {};
}

/* =========================
   Receipt Modal (Printable)
   ========================= */
function ReceiptModal({ open, onClose, receipt, logoSrc }) {
  const receiptRef = React.useRef(null);
  const [qrDataUrl, setQrDataUrl] = React.useState("");

  const [customerName, setCustomerName] = React.useState("");
  const [customerPhone, setCustomerPhone] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setCustomerName(receipt?.customerName || "");
    setCustomerPhone(receipt?.customerPhone || "");
  }, [open, receipt?.customerName, receipt?.customerPhone]);

  React.useEffect(() => {
    if (!open) return;

    const handle = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handlePrint();
      }
    };

    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  React.useEffect(() => {
    let alive = true;
    const url = receipt?.website || "https://www.sts-lb.net";

    (async () => {
      try {
        const data = await QRCode.toDataURL(url, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 170,
        });
        if (alive) setQrDataUrl(data);
      } catch {
        if (alive) setQrDataUrl("");
      }
    })();

    return () => {
      alive = false;
    };
  }, [receipt?.website]);

  if (!open) return null;

  const r = receipt || {};
  const lines = Array.isArray(r.lines) ? r.lines : [];
  const totalQty = lines.reduce((sum, ln) => sum + (Number(ln.qty) || 0), 0);

  const dtBeirut = new Date(r.date || Date.now());
  const dtStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Beirut",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(dtBeirut);

  const moneyLocal = (v) => {
    const num = Number(v);
    return `$${(Number.isFinite(num) ? num : 0).toFixed(2)}`;
  };

  function handlePrint() {
    const html = receiptRef.current?.innerHTML || "";
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) return;

    w.document.open();
    w.document.write(`
      <html>
        <head>
          <title>Smart Tech Solutions — Receipt</title>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <style>
            :root{ --ink:#0b1220; --muted:#536073; --line:#e7ebf2; --soft:#f7f9fc; }
            @page { size: A4; margin: 6mm; }
            *{box-sizing:border-box}
            body{ margin:0; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Noto Sans"; color:var(--ink); background:white; }
            #print-root{ width:100%; }
            @media print{ .paper{ border:none !important; box-shadow:none !important; border-radius:0 !important; } }
            .paper{ width:100%; margin:0; border: 1px solid var(--line); border-radius: 16px; overflow:hidden; box-shadow: 0 10px 28px rgba(11,18,32,0.08); }
            .pad{padding:10px 12px}
            .top{ border-bottom: 1px solid var(--line); background: #f6f8ff; }
            .brand{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
            .brand-left{display:flex;align-items:center;gap:10px;min-width:0}
            .logo{ width:44px;height:44px; object-fit:contain; border-radius:10px; background:white; border:1px solid var(--line); padding:6px; flex:0 0 44px; }
            .title{font-weight:900;letter-spacing:-0.02em;font-size:15px;margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
            .subtitle{margin:3px 0 0;font-size:10px;color:var(--muted)}
            .meta{ margin-top:10px; display:grid; grid-template-columns:1fr 1fr; gap:8px; }
            .pill{ background: rgba(255,255,255,0.85); border: 1px solid var(--line); border-radius:12px; padding:8px; }
            .k{font-size:9px;color:var(--muted);text-transform:uppercase;letter-spacing:0.18em}
            .v{margin-top:3px;font-size:11px;font-weight:800;word-break:break-word}
            table{width:100%;border-collapse:collapse}
            th{ text-align:left; font-size:9px; color:var(--muted); letter-spacing:0.16em; text-transform:uppercase; padding:7px 0; border-bottom:1px solid var(--line); }
            td{ padding:7px 0; border-bottom:1px solid var(--line); vertical-align:top; font-size:11px; }
            .qty,.amt{text-align:right}
            .name{font-weight:800;margin:0}
            .desc{margin-top:2px;color:var(--muted);font-size:10px;line-height:1.2;white-space:pre-wrap}
            .totalBox{ background:var(--soft); border:1px solid var(--line); border-radius:12px; padding:10px; display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
            .totalLabel{ font-size:9px; color:var(--muted); letter-spacing:0.16em; text-transform:uppercase; }
            .grand{font-size:16px;font-weight:900;letter-spacing:-0.02em;margin-top:5px}
            .qrWrap{ display:flex; align-items:center; justify-content:space-between; gap:10px; margin-top:10px; }
            .qrCard{ border:1px solid var(--line); border-radius:12px; padding:8px; background:white; display:flex; align-items:center; gap:10px; }
            .qrImg{ width:66px; height:66px; object-fit:contain; border-radius:10px; border:1px solid var(--line); padding:6px; background:white; }
            .qrText .big{font-weight:900;font-size:11px;margin:0}
            .qrText .small{margin-top:3px;font-size:10px;color:var(--muted);word-break:break-all}
            .signWrap{ margin-top:10px; border:1px dashed var(--line); border-radius:12px; padding:10px; background:white; }
            .signTitle{ font-size:9px; color:var(--muted); letter-spacing:0.16em; text-transform:uppercase; margin:0 0 8px; }
            .signLine{height:30px;border-bottom:2px solid var(--ink);opacity:0.9}
            .signHint{margin-top:6px;font-size:10px;color:var(--muted)}
            .footer{ text-align:center; color:var(--muted); font-size:10px; padding:10px 12px; background:white; }
            .twoColItems .items-grid{ display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-top:4px; }
            .twoColItems .items-grid table{ width:100%; }
            .twoColItems .items-grid td{ padding:6px 0; font-size:10.5px; }
            .twoColItems .items-grid th{ padding:6px 0; font-size:8.5px; }
            .totalBox, .qrWrap, .signWrap { break-inside: avoid; page-break-inside: avoid; }
          </style>
        </head>
        <body>
          <div id="print-root">${html}</div>
          <script>
            function pageHeightPx(){ return window.innerHeight; }
            function findItemsTable(){
              const tables = Array.from(document.querySelectorAll("table"));
              if (!tables.length) return null;
              let best = tables[0], bestRows = 0;
              for (const t of tables){
                const rows = t.querySelectorAll("tbody tr").length;
                if (rows > bestRows){ bestRows = rows; best = t; }
              }
              return best;
            }
            function splitTableIntoTwoColumns(table){
              const tbody = table.querySelector("tbody");
              const thead = table.querySelector("thead");
              if (!tbody) return false;
              const rows = Array.from(tbody.querySelectorAll("tr"));
              if (rows.length < 10) return false;
              const half = Math.ceil(rows.length / 2);
              const t1 = document.createElement("table");
              const t2 = document.createElement("table");
              if (thead){ t1.appendChild(thead.cloneNode(true)); t2.appendChild(thead.cloneNode(true)); }
              const b1 = document.createElement("tbody");
              const b2 = document.createElement("tbody");
              rows.slice(0, half).forEach(r => b1.appendChild(r.cloneNode(true)));
              rows.slice(half).forEach(r => b2.appendChild(r.cloneNode(true)));
              t1.appendChild(b1); t2.appendChild(b2);
              const grid = document.createElement("div");
              grid.className = "items-grid";
              grid.appendChild(t1); grid.appendChild(t2);
              table.parentNode.insertBefore(grid, table);
              table.remove();
              return true;
            }
            function contentOverflowsOnePage(){
              const root = document.getElementById("print-root");
              if (!root) return false;
              return root.scrollHeight > pageHeightPx();
            }
            function shrinkFontStep(step){
              const root = document.getElementById("print-root");
              const current = parseFloat(root.dataset.baseFont || "11");
              const next = Math.max(9, current - step);
              root.dataset.baseFont = String(next);
              root.style.fontSize = next + "px";
            }
            function fitOnePageNoScaling(){
              const root = document.getElementById("print-root");
              if (!root) return;
              if (!contentOverflowsOnePage()) return;
              const paper = root.querySelector(".paper") || root;
              paper.classList.add("twoColItems");
              const itemsTable = findItemsTable();
              if (itemsTable) splitTableIntoTwoColumns(itemsTable);
              if (!contentOverflowsOnePage()) return;
              for (let i=0;i<6;i++){
                if (!contentOverflowsOnePage()) break;
                shrinkFontStep(0.4);
              }
            }
            window.onload = () => {
              fitOnePageNoScaling();
              setTimeout(() => {
                window.print();
                window.onafterprint = () => window.close();
              }, 60);
            };
          </script>
        </body>
      </html>
    `);
    w.document.close();
  }

  const invoiceShown = r.invoiceNo || r.receiptNo || r.receipt_no || "—"; // ✅ support both names

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute inset-0 grid place-items-center p-4 md:p-6">
        <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#0b1220]/95 text-white shadow-2xl overflow-hidden max-h-[85vh] flex flex-col">
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between shrink-0">
            <div>
              <div className="font-semibold text-lg">Receipt</div>
              <div className="text-white/60 text-sm">Add customer details before printing</div>
            </div>
            <button
              onClick={onClose}
              className="h-10 w-10 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80"
              title="Close"
            >
              ✕
            </button>
          </div>

          <div className="p-6 overflow-y-auto flex-1 overscroll-contain space-y-4">
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-4">
              <div className="text-white font-semibold">Customer Details</div>
              <div className="text-white/60 text-sm">Optional — will appear on the printed receipt</div>

              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">Customer name</label>
                  <input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="e.g. Ahmad / Sarah"
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 outline-none text-white placeholder:text-white/30 focus:border-white/20"
                  />
                </div>

                <div>
                  <label className="block text-xs text-white/60 mb-1">Phone number</label>
                  <input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="e.g. 70 216 395"
                    className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3 outline-none text-white placeholder:text-white/30 focus:border-white/20"
                  />
                </div>
              </div>
            </div>

            <div ref={receiptRef}>
              <div className="paper">
                <div className="top pad">
                  <div className="brand">
                    <div className="brand-left">
                      {logoSrc ? <img className="logo" src={logoSrc} alt="STS" /> : null}
                      <div style={{ minWidth: 0 }}>
                        <p className="title">Smart Tech Solutions</p>
                        <p className="subtitle">Sales Receipt</p>
                      </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.18em", color: "#536073", textTransform: "uppercase" }}>
                        Invoice
                      </div>
                      <div style={{ fontWeight: 900, fontSize: 12 }}>{invoiceShown}</div>
                    </div>
                  </div>

                  <div className="meta">
                    <div className="pill">
                      <div className="k">Date & Time</div>
                      <div className="v">{dtStr}</div>
                    </div>
                    <div className="pill">
                      <div className="k">Total Quantity</div>
                      <div className="v">{totalQty}</div>
                    </div>
                  </div>

                  <div className="meta" style={{ marginTop: 10 }}>
                    <div className="pill">
                      <div className="k">Customer</div>
                      <div className="v">{customerName?.trim() ? customerName.trim() : "—"}</div>
                    </div>
                    <div className="pill">
                      <div className="k">Phone</div>
                      <div className="v">{customerPhone?.trim() ? customerPhone.trim() : "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="pad">
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="qty">Qty</th>
                        <th className="amt">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln, i) => {
                        const qty = Number(ln.qty) || 0;
                        const sell = Number(ln.sell_price) || 0;
                        const lineTotal = sell * qty;

                        return (
                          <tr key={i}>
                            <td>
                              <div className="name">{ln.name || "—"}</div>
                              {ln.description ? <div className="desc">{ln.description}</div> : null}
                            </td>
                            <td className="qty">{qty}</td>
                            <td className="amt">{moneyLocal(lineTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div style={{ height: 14 }} />

                  <div className="totalBox">
                    <div>
                      <div className="totalLabel">Grand Total</div>
                      <div className="grand">{moneyLocal(r.total_sell || 0)}</div>
                    </div>
                    <div style={{ textAlign: "right", color: "#536073", fontSize: 11, lineHeight: 1.25 }}>
                      Thank you for your purchase.
                      <div>We appreciate your trust.</div>
                    </div>
                  </div>

                  <div className="qrWrap">
                    <div className="qrCard" style={{ flex: 1 }}>
                      {qrDataUrl ? <img className="qrImg" src={qrDataUrl} alt="QR" /> : null}
                      <div className="qrText">
                        <p className="big">Scan to visit our website</p>
                        <div className="small">{r.website || "https://www.sts-lb.net"}</div>
                      </div>
                    </div>
                  </div>

                  <div className="signWrap">
                    <p className="signTitle">Customer Signature</p>
                    <div className="signLine"></div>
                    <div className="signHint">Sign here to confirm receipt of items.</div>
                  </div>
                </div>

                <div className="footer">Smart Tech Solutions • Websites • CCTV • Repairs • Support</div>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 shrink-0">
            <div className="mt-2 flex gap-2">
              <button
                onClick={handlePrint}
                className="flex-1 py-3 rounded-2xl font-semibold border border-white/20 bg-white/10 hover:bg-white/15"
              >
                Print Receipt
              </button>
              <button
                onClick={onClose}
                className="px-5 py-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80"
              >
                Close
              </button>
            </div>

            <div className="mt-3 text-xs text-white/50">Shortcut: Ctrl/Cmd + P inside the receipt.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Dashboard (POS Cart + Receipt + History)
   ========================= */
export default function Dashboard() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState("");

  const [imgViewerOpen, setImgViewerOpen] = React.useState(false);
  const [imgViewerSrc, setImgViewerSrc] = React.useState("");
  const [imgViewerAlt, setImgViewerAlt] = React.useState("");

  const [search, setSearch] = React.useState("");

  const [cart, setCart] = React.useState({});
  const [checkingOut, setCheckingOut] = React.useState(false);
  const [cartMsg, setCartMsg] = React.useState("");
  const [cartErr, setCartErr] = React.useState("");

  // receipt state
  const [receiptOpen, setReceiptOpen] = React.useState(false);
  const [receipt, setReceipt] = React.useState(null);

  // ✅ NEW: history state
  const [leftTab, setLeftTab] = React.useState("cart"); // "cart" | "history"
  const [sales, setSales] = React.useState([]);
  const [salesLoading, setSalesLoading] = React.useState(false);
  const [salesErr, setSalesErr] = React.useState("");
  const [salesOffset, setSalesOffset] = React.useState(0);
  const SALES_LIMIT = 30;

  function getStock(item) {
    return n(item?.quantity ?? item?.qty ?? item?.stock ?? item?.in_stock ?? 0);
  }

  const refresh = React.useCallback(async () => {
    setErr("");
    setLoading(true);

    try {
      const data = await api.listItems("");
      const arr = Array.isArray(data) ? data : [];
      if (arr.length === 0) {
        const direct = await fetchItemsDirect();
        setItems(direct);
      } else {
        setItems(arr);
      }
    } catch (e) {
      try {
        const direct = await fetchItemsDirect();
        setItems(direct);
      } catch (e2) {
        console.error(e2);
        setErr(e2?.message || "Failed to load items");
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const total = items.length;
  const totalQty = items.reduce((sum, it) => sum + getStock(it), 0);
  const totalValue = items.reduce((sum, it) => sum + getStock(it) * n(it.unit_cost), 0);

  const visibleItems = React.useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    let arr = [...items];

    const hasDate = arr.some((x) => x.updated_at || x.created_at);
    if (hasDate) {
      arr.sort((a, b) => {
        const ad = new Date(a.updated_at || a.created_at || 0).getTime();
        const bd = new Date(b.updated_at || b.created_at || 0).getTime();
        return bd - ad;
      });
    }

    if (!q) return arr;

    return arr.filter((it) => {
      const name = String(it.name || "").toLowerCase();
      const barcode = String(it.barcode || "").toLowerCase();
      const desc = String(it.description || "").toLowerCase();
      return name.includes(q) || barcode.includes(q) || desc.includes(q);
    });
  }, [items, search]);

  const cartList = React.useMemo(() => Object.values(cart), [cart]);

  const cartTotals = React.useMemo(() => {
    const totalCost = cartList.reduce((sum, row) => sum + n(row.item.unit_cost) * n(row.qty), 0);
    const totalSell = cartList.reduce((sum, row) => sum + n(row.item.sell_price) * n(row.qty), 0);
    const profit = totalSell - totalCost;
    const lines = cartList.reduce((sum, row) => sum + n(row.qty), 0);
    return { totalCost, totalSell, profit, lines };
  }, [cartList]);

  function addToCart(item) {
    setCartMsg("");
    setCartErr("");

    const stock = getStock(item);
    if (stock <= 0) return;

    setCart((prev) => {
      const current = prev[item.id]?.qty || 0;
      const next = Math.min(stock, n(current) + 1);
      return { ...prev, [item.id]: { item, qty: next } };
    });
  }

  function openImageViewer(src, alt) {
    if (!src) return;
    setImgViewerSrc(src);
    setImgViewerAlt(alt || "Item image");
    setImgViewerOpen(true);
  }

  function getItemImageSrc(item) {
    const raw =
      item?.image_url ||
      item?.imageUrl ||
      item?.image ||
      item?.img_url ||
      item?.imgUrl ||
      item?.img ||
      item?.photo_url ||
      item?.photoUrl ||
      item?.photo ||
      item?.thumbnail ||
      "";

    if (!raw) return "";
    try {
      return apiUrl(raw);
    } catch {
      return raw;
    }
  }

  function inc(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const row = prev[id];
      if (!row) return prev;

      const stockNow = getStock(items.find((x) => x.id === id) ?? row.item);
      const next = Math.min(stockNow, n(row.qty) + 1);

      return { ...prev, [id]: { ...row, qty: next } };
    });
  }

  function dec(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const row = prev[id];
      if (!row) return prev;
      const next = n(row.qty) - 1;
      if (next <= 0) {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      }
      return { ...prev, [id]: { ...row, qty: next } };
    });
  }

  function removeFromCart(id) {
    setCartMsg("");
    setCartErr("");
    setCart((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  }

  // ✅ NEW: load history list
  async function loadSales({ reset = false } = {}) {
    setSalesErr("");
    setSalesLoading(true);
    try {
      const offset = reset ? 0 : salesOffset;
      const rows = await fetchSalesDirect({ limit: SALES_LIMIT, offset });
      setSales((prev) => (reset ? rows : [...prev, ...rows]));
      setSalesOffset(offset + rows.length);
    } catch (e) {
      console.error(e);
      setSalesErr(e?.message || "Failed to load history");
    } finally {
      setSalesLoading(false);
    }
  }

  // load once when user opens History tab
  React.useEffect(() => {
    if (leftTab !== "history") return;
    if (sales.length > 0) return;
    loadSales({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftTab]);

  // ✅ NEW: open an old sale as a receipt
  async function openSaleReceipt(saleId) {
    setSalesErr("");
    setSalesLoading(true);
    try {
      const data = await fetchSaleDetailsDirect(saleId);
      const r = data?.receipt || null;
      if (!r) throw new Error("Receipt data missing from backend response");
      setReceipt(r);
      setReceiptOpen(true);
    } catch (e) {
      console.error(e);
      setSalesErr(e?.message || "Failed to open receipt");
    } finally {
      setSalesLoading(false);
    }
  }

  async function checkout() {
    setCartMsg("");
    setCartErr("");

    if (cartList.length === 0) {
      setCartErr("Cart is empty.");
      return;
    }

    setCheckingOut(true);
    try {
      const receiptLines = cartList.map((row) => ({
        id: row.item.id,
        name: row.item.name,
        description: row.item.description || row.item.notes || "",
        qty: n(row.qty),
        sell_price: n(row.item.sell_price),
      }));

      const result = await checkoutDirect(cartList);

      const now = new Date();
      const receiptNo =
        result.receipt_no ||
        `STS-${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}-${Math.floor(1000 + Math.random() * 9000)}`;

      const r = {
        receiptNo,          // ✅ keep your original
        invoiceNo: receiptNo, // ✅ also set invoiceNo so header never shows "—"
        date: now.toISOString(),
        website: "https://www.sts-lb.net",
        lines: receiptLines,
        total_cost: result.total_cost ?? cartTotals.totalCost,
        total_sell: result.total_sell ?? cartTotals.totalSell,
        profit: result.profit ?? cartTotals.profit,
        total_lines: receiptLines.length,
      };

      setCart({});
      await refresh();

      setCartMsg(`Sale completed ✅`);

      // ✅ refresh history (prepend newest) if user already loaded it
      if (sales.length > 0) {
        loadSales({ reset: true });
      }

      setReceipt(r);
      setReceiptOpen(true);
    } catch (e) {
      console.error(e);
      setCartErr(e?.message || "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  }

  return (
    <div className="space-y-6">
      <ReceiptModal open={receiptOpen} receipt={receipt} onClose={() => setReceiptOpen(false)} logoSrc={ReceiptLogo} />

      {imgViewerOpen ? (
        <div className="fixed inset-0 z-[120]">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setImgViewerOpen(false)} />
          <div className="absolute inset-0 grid place-items-center p-4">
            <div className="relative w-full max-w-3xl rounded-3xl border border-white/10 bg-[#0b1220]/95 shadow-2xl overflow-hidden">
              <button
                onClick={() => setImgViewerOpen(false)}
                className="absolute top-3 right-3 h-10 w-10 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 z-10"
                title="Close"
                type="button"
              >
                ✕
              </button>

              <div className="p-4 md:p-6">
                <div className="w-full max-h-[75vh] overflow-auto flex items-center justify-center">
                  <img
                    src={imgViewerSrc}
                    alt={imgViewerAlt}
                    className="max-w-full max-h-[75vh] object-contain rounded-2xl border border-white/10"
                  />
                </div>
                <div className="mt-3 text-sm text-white/70 truncate">{imgViewerAlt}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid md:grid-cols-3 gap-4">
        <StatCard title="Total Items" value={loading ? "…" : total} hint="All inventory products" icon="📦" />
        <StatCard title="Total Quantity" value={loading ? "…" : totalQty} hint="Sum of all stock" icon="🧮" />
        <StatCard title="Inventory Cost Value" value={loading ? "…" : money(totalValue)} hint="quantity × unit_cost" icon="💰" />
      </div>

      {err ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-100 px-4 py-3 text-sm flex items-center justify-between gap-3">
          <div>
            <div className="font-semibold">Could not load items</div>
            <div className="opacity-90">{err}</div>
          </div>
          <button onClick={refresh} className="px-4 py-2 rounded-2xl border border-white/20 bg-white/10 text-white hover:bg-white/15">
            Retry
          </button>
        </div>
      ) : null}

      <div className="mt-2 grid xl:grid-cols-[360px_1fr] gap-6 items-start">
        {/* Left column: Cart / History */}
        <div className="rounded-3xl border border-white/10 bg-white/5 overflow-hidden sticky top-6">
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-semibold text-white">POS</div>
                <div className="text-xs text-white/60">{leftTab === "cart" ? "Checkout then print receipt" : "Completed orders history"}</div>
              </div>

              <div className="inline-flex rounded-2xl border border-white/10 bg-white/5 p-1">
                <button
                  type="button"
                  onClick={() => setLeftTab("cart")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold ${
                    leftTab === "cart" ? "bg-white/15 text-white" : "text-white/70 hover:text-white"
                  }`}
                >
                  Cart
                </button>
                <button
                  type="button"
                  onClick={() => setLeftTab("history")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold ${
                    leftTab === "history" ? "bg-white/15 text-white" : "text-white/70 hover:text-white"
                  }`}
                >
                  History
                </button>
              </div>
            </div>

            <div className="mt-3 text-xs text-white/60">
              {leftTab === "cart" ? `Items in cart: ${cartTotals.lines}` : `Loaded sales: ${sales.length}`}
            </div>
          </div>

          <div className="p-5 space-y-4">
            {leftTab === "cart" ? (
              <>
                {cartList.length === 0 ? (
                  <div className="text-sm text-white/60">Cart is empty. Add items from the list.</div>
                ) : (
                  <div className="space-y-3">
                    {cartList.map((row) => {
                      const it = row.item;
                      return (
                        <div key={it.id} className="rounded-2xl border border-white/10 bg-white/5 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-white truncate">{it.name}</div>
                              <div className="text-xs text-white/50 truncate">{it.barcode || "—"}</div>
                            </div>
                            <button
                              className="text-xs rounded-xl px-3 py-1 border border-white/15 hover:bg-white/10 text-white/80"
                              onClick={() => removeFromCart(it.id)}
                              type="button"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <button
                                className="h-9 w-9 rounded-xl border border-white/15 hover:bg-white/10 text-white"
                                onClick={() => dec(it.id)}
                                type="button"
                              >
                                −
                              </button>
                              <div className="h-9 min-w-[52px] px-3 rounded-xl border border-white/10 bg-white/5 flex items-center justify-center text-white font-semibold">
                                {row.qty}
                              </div>
                              <button
                                className="h-9 w-9 rounded-xl border border-white/15 hover:bg-white/10 text-white"
                                onClick={() => inc(it.id)}
                                type="button"
                              >
                                +
                              </button>
                            </div>

                            <div className="text-sm text-white font-semibold">{money(n(it.sell_price) * n(row.qty))}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* (Admin-only totals; you can keep or hide later) */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center justify-between text-sm text-white/80">
                    <span>Total Sell</span>
                    <span className="font-semibold text-white">{money(cartTotals.totalSell)}</span>
                  </div>
                </div>

                {!!cartMsg && <div className="text-xs text-emerald-200">{cartMsg}</div>}
                {!!cartErr && <div className="text-xs text-red-200">{cartErr}</div>}

                <button
                  className="w-full h-11 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={checkout}
                  disabled={cartList.length === 0 || checkingOut}
                  type="button"
                >
                  {checkingOut ? "Processing..." : "Checkout (Complete Sale)"}
                </button>

                <div className="text-xs text-white/50">After checkout, a printable receipt will pop up.</div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => loadSales({ reset: true })}
                    className="h-10 px-4 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold"
                    disabled={salesLoading}
                  >
                    {salesLoading ? "Loading..." : "Refresh"}
                  </button>

                  <button
                    type="button"
                    onClick={() => loadSales({ reset: false })}
                    className="h-10 px-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/90 font-semibold"
                    disabled={salesLoading}
                  >
                    Load more
                  </button>
                </div>

                {!!salesErr && <div className="text-xs text-red-200">{salesErr}</div>}

                {sales.length === 0 && !salesLoading ? (
                  <div className="text-sm text-white/60">No sales yet (or nothing loaded).</div>
                ) : (
                  <div className="space-y-2">
                    {sales.map((s) => {
                      const id = s.id ?? s.sale_id;
                      const created = s.created_at || s.createdAt || s.date || s.created || null;
                      const totalSell = n(s.total_sell);
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => openSaleReceipt(id)}
                          className="w-full text-left rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-white truncate">Sale #{id}</div>
                              <div className="text-xs text-white/60 truncate">{created ? formatDateTime(created) : "—"}</div>
                            </div>
                            <div className="text-sm font-semibold text-white">{money(totalSell)}</div>
                          </div>
                          <div className="mt-2 text-xs text-white/50">Click to open receipt & print</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {salesLoading ? <div className="text-xs text-white/50">Loading…</div> : null}
              </>
            )}
          </div>
        </div>

        {/* Right column: Items */}
        <div className="rounded-3xl border border-white/10 bg-white/5 overflow-hidden">
          <div className="p-5 border-b border-white/10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <div className="font-semibold text-white">Items</div>
              <div className="text-xs text-white">Add products quickly to cart</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or barcode…"
                className="h-11 w-full sm:w-[360px] rounded-2xl bg-black/20 border border-white/10 px-4 text-white placeholder:text-white/40 outline-none focus:border-white/25"
              />
              <button
                className="h-11 rounded-2xl px-5 bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold"
                onClick={refresh}
                type="button"
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <thead className="bg-white/10">
                <tr className="text-xs text-white/80">
                  <th className="text-left font-semibold px-5 py-3 w-[44%]">Item</th>
                  <th className="text-left font-semibold px-4 py-3 w-[22%]">Barcode</th>
                  <th className="text-right font-semibold px-4 py-3 w-[10%]">Qty</th>
                  <th className="text-right font-semibold px-4 py-3 w-[12%]">Unit</th>
                  <th className="text-right font-semibold px-4 py-3 w-[12%]">Sell</th>
                  <th className="text-right font-semibold px-5 py-3 w-[120px]">Add</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/10">
                {visibleItems.map((item) => {
                  const imgSrc = getItemImageSrc(item);
                  return (
                    <tr key={item.id} className="hover:bg-white/5">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <button
                            type="button"
                            onClick={() => openImageViewer(imgSrc, item.name)}
                            className="h-11 w-11 rounded-2xl border border-white/10 bg-white/5 overflow-hidden flex items-center justify-center flex-shrink-0 hover:border-white/25 hover:bg-white/10 transition"
                            title="View image"
                          >
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={item.name}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div className="text-xs text-white/50">No Img</div>
                            )}
                          </button>

                          <div className="min-w-0">
                            <div className="text-white font-semibold truncate">{item.name}</div>
                            {item.description ? <div className="text-xs text-white/50 truncate">{item.description}</div> : null}
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4 text-white/80 truncate">{item.barcode || "—"}</td>
                      <td className="px-4 py-4 text-right text-white">{getStock(item)}</td>
                      <td className="px-4 py-4 text-right text-white">{money(item.unit_cost)}</td>
                      <td className="px-4 py-4 text-right text-white">{money(item.sell_price)}</td>

                      <td className="px-5 py-4 text-right">
                        <button
                          className="inline-flex items-center justify-center h-10 px-4 rounded-2xl bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold"
                          onClick={() => addToCart(item)}
                          type="button"
                        >
                          + Add
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {loading && <div className="p-5 text-sm text-white/60">Loading…</div>}
        </div>
      </div>
    </div>
  );
}
